<template>
    <ThePage>
        <template v-slot:goback>
            <router-link :to="{ name: 'vms' }"><i class="tlm-icon-chevron-left small mr-2" />Вернуться к списку автоматов</router-link>
        </template>

        <main class="d-flex flex-column flex-lg-row align-items-stretch">
            <div class="card card-vm">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                        <span class="vm-name text-truncate">{{ vm.name }}</span>

                        <router-link :to="{ name: 'vm.settings' }" class="btn btn-link px-2">
                            <i class="tlm-icon-cog" />
                        </router-link>
                    </div>

                    <div class="vm-details">
                        <small>
                            <span class="text-muted mr-2 d-inline-block">Серийный номер:</span> {{ vm.serial }}
                        </small>
                    </div>
                </div>
            </div>

            <div v-feature="'lh_loyalty'" class="card card-vm-lh_loyalty d-flex flex-row flex-lg-column ml-0 ml-lg-3 mt-2 mt-lg-0">
                <router-link :to="{ name: 'vm.loyalty' }" class="btn btn-lg btn-link bg-white px-3 mr-2 mr-lg-0 mb-0 mb-lg-0 mt-lg-2 text-decoration-none d-flex justify-content-between align-items-center">
                    <span class="text-left">Программа лояльности</span> <i class="tlm-icon-chevron-right ml-4" />
                </router-link>

                <span class="btn-lg bg-white px-3 d-flex justify-content-between align-items-center flex-fill">
                    <span v-if="vm.loyalty" class="text-success">
                        <i class="tlm-icon-check mr-1 d-none d-lg-inline" /> Включена
                    </span>

                    <span v-else class="text-muted">Выключена</span>
                </span>
            </div>

            <div v-feature="'loading'" class="d-flex flex-column flex-lg-row align-self-stretch ml-0 ml-lg-3 mt-2 mt-lg-0">
                <div v-if="vm.loading && vm.loading.products.loading !== null" class="card card-vm-level">
                    <div class="card-body">
                        <vm-loading-level :loading="vm.loading.products.loading" :max="vm.loading.products.max" vertical type="spirals" />
                    </div>
                </div>

                <div v-if="vm.loading && vm.loading.recipes.loading !== null" class="card card-vm-level ml-0 ml-lg-2 mt-2 mt-lg-0">
                    <div class="card-body">
                        <vm-loading-level :loading="vm.loading.recipes.loading" :max="vm.loading.recipes.max" vertical type="components" />
                    </div>
                </div>
            </div>

            <div class="d-flex flex-row flex-lg-column ml-0 ml-lg-3 mt-2 mt-lg-0">
                <router-link :to="{ name: 'vm.layout' }" class="btn btn-lg btn-link bg-white px-3 mr-2 mr-lg-0 mb-0 mb-lg-2 text-decoration-none d-flex justify-content-between align-items-center">
                    Продукция <i class="tlm-icon-chevron-right ml-4" />
                </router-link>

                <router-link v-feature="'loading'" :to="{ name: 'vm.loading' }" class="btn btn-lg btn-link bg-white px-3 d-flex justify-content-between align-items-center flex-fill">
                    Загрузка <i class="tlm-icon-chevron-right ml-4" />
                </router-link>
            </div>
        </main>

        <dash-graph class="my-5" :range.sync="range" :payment-type.sync="ptype" :chart="chart">
            <template v-slot:help>
                <a target="_blank" :href="getKBLink('vm_card')" class="btn btn-outline-primary text-center"><i class="tlm-icon-question-circle" /></a>
            </template>
        </dash-graph>

        <div class="d-flex justify-content-between my-3">
            <div>
                <span class="font-weight-bold">Список продаж</span>
                <vm-bus-numbering-scheme-button ref="busNumberingSchemeButton" v-model="busNumberingScheme" class="ml-4" />
            </div>

            <router-link v-if="crtReady" :to="{ name: 'vm.correction' }" class="btn btn-primary ml-2 d-none d-sm-block"><i class="tlm-icon-plus mr-2" /> Создать чек коррекции</router-link>
        </div>

        <vend-list :vends="vends" :has-crt="hasCrt" :currency="currency">
            <template v-slot:moar>
                <infinite-loading spinner="waveDots" :identifier="$route.fullPath" @infinite="loadVends">
                    <template v-slot:no-more>Загружены все продажи за выбранный период.</template>
                    <template v-slot:no-results>Нет продаж за выбранный период.</template>
                </infinite-loading>
            </template>
        </vend-list>

        <router-view />
    </ThePage>
</template>

<script>
import ThePage from '@/components/ThePage'
import DashGraph from '@/components/DashGraph'
import VendList from '@/components/VendList'
import InfiniteLoading from 'vue-infinite-loading'
import VmLoadingLevel from '@/components/VmLoadingLevel'

import store from '@/store'
import { toastError } from '@/dialogs'

import DateCacheService, { oneWeekAgoRange } from '@/services/DateCacheService'
import KBLinksService from '@/services/KBLinksService'

import VmBusNumberingSchemeButton from '@/components/VmBusNumberingSchemeButton'

function resolveDefaultRange() {
    return DateCacheService.getFormatted('dash', oneWeekAgoRange)
}

const beforeRoute = async (to, from, next) => {
    if (!to.query.since || !to.query.until) {
        return next({ name: to.name, params: { id: to.params.id }, query: resolveDefaultRange() })
    }

    // LITE-114
    // Некоторые пользователи (или сотрудники) редко пользуются Лайтом/ЛХ.
    // У них может в локалсторадже остаться очень старый интервал дат, бек на него ругнется своей валидацией.
    if (to.query.since && DateCacheService.isTooFar(to.query.since)) {
        DateCacheService.forget('dash')
        return next({ name: to.name, params: { id: to.params.id } })
    }

    const done = await Promise.all([
        store.dispatch('vm/fetch', to.params.id),
        store.dispatch('vm/vends/reset'),
        store.dispatch('vm/vends/fetchChart', { id: to.params.id, params: to.query })
    ])

    if (done[0]) {
        next()
    } else {
        toastError(`ТА #${to.params.id} не найден`)
        next({ name: 'vms' })
    }
}

export default {
    components: {
        ThePage,
        DashGraph,
        VendList,
        InfiniteLoading,
        VmLoadingLevel,
        VmBusNumberingSchemeButton
    },

    props: {
        filter: {
            type: Object,
            default: _ => ({ })
        }
    },

    computed: {
        vm() {
            return this.$store.state.vm.data
        },

        vends() {
            return this.$store.state.vm.vends.data
        },

        currency() {
            return this.$store.state.company.profile.data.balance_currency || null
        },

        range: {
            set({ alias, ...range }) {
                alias
                    ? DateCacheService.setAlias('dash', alias)
                    : DateCacheService.set('dash', range)

                this.applyFilter({ ...range, page: 1 })
            },

            get() {
                return this.filter
            }
        },

        ptype: {
            set(ptype) {
                this.applyFilter({ ptype, page: 1 })
            },

            get() {
                return this.filter.ptype || 0
            }
        },

        chart() {
            return this.$store.state.vm.vends.chart
        },

        hasCrt() {
            return this.$store.getters['company/hasCrt']
        },

        crtReady() {
            return this.$store.getters['company/crtReady']
        },

        busNumberingScheme: {
            get() {
                return this.vm.bus_numbering_scheme
            },

            async set(bus_numbering_scheme) {
                const done = await store.dispatch('vm/save', { id: this.$route.params.id, bus_numbering_scheme })

                if (done) {
                    this.$refs.busNumberingSchemeButton.close()
                }
            }
        }
    },

    methods: {
        async loadVends(infinite) {
            const hasMore = await store.dispatch('vm/vends/load', { id: this.$route.params.id, params: this.$route.query })

            if (hasMore) {
                infinite.loaded()
            } else {
                if (this.vends.length > 0) {
                    infinite.loaded()
                }

                infinite.complete()
            }
        },

        getKBLink(key) {
            return KBLinksService.get(key)
        }
    },

    beforeRouteEnter: beforeRoute,
    beforeRouteUpdate: beforeRoute,

    beforeRouteLeave(to, from, next) {
        store.commit('vm/SET_DATA', {})
        store.dispatch('vm/vends/reset')
        next()
    }
}
</script>
